/**
 * Invoices API – customer-facing subset. Mirrors
 * vin-pulse-os-web/src/services/invoicesApi.ts, trimmed to the endpoints a
 * Customer role can actually call:
 *   GET /api/v1/customers/me/invoices  – the signed-in customer's own invoices
 *   GET /api/v1/invoices/:id           – single invoice detail (customer-ownership scoped)
 *   GET /api/v1/invoices/:id/pdf       – real PDF download (customer-ownership scoped)
 *
 * Plain GET /invoices (list-all) is Admin/Sales Rep/Call Center only
 * server-side (routes/invoices.ts) — deliberately not exposed here.
 */

import { apiClient } from './apiClient';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

export interface Invoice {
  id: number;
  order_id: number;
  invoice_number: string;
  invoice_date: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  status: InvoiceStatus;
  order_data?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // present on the customer-self list endpoint
  order_status?: string;
}

export interface ListInvoicesFilters {
  status?: InvoiceStatus;
  limit?: number;
  offset?: number;
}

function normalize(raw: any): { data: Invoice[]; total: number; limit: number; offset: number } {
  const inner = raw?.data?.invoices !== undefined ? raw.data : raw;
  return {
    data: inner?.invoices ?? [],
    total: inner?.total ?? 0,
    limit: inner?.limit ?? 20,
    offset: inner?.offset ?? 0,
  };
}

export async function listMyInvoices(filters: ListInvoicesFilters = {}): Promise<{
  data: Invoice[];
  total: number;
  limit: number;
  offset: number;
}> {
  const raw = await apiClient.get<any>('/customers/me/invoices', filters as Record<string, any>);
  return normalize(raw);
}

export async function getInvoice(id: number | string): Promise<Invoice> {
  return apiClient.get<Invoice>(`/invoices/${id}`);
}

/**
 * VP-CUST-005: real invoice PDF, scoped server-side to the invoice's own
 * customer. Backend: GET /invoices/:id/pdf.
 */
export async function downloadInvoicePdf(id: number | string): Promise<Blob> {
  return apiClient.downloadBlob(`/invoices/${id}/pdf`);
}
